import { useEffect, useRef, useState } from 'react';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from './Icons';
import { localDateStr } from './callUtils';

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Dates flow through this component (and the fromDate/toDate state it's fed)
// as local 'YYYY-MM-DD' strings, same as the native <input type="date"> it
// replaces — parsed here as a local (not UTC) Date so day-of-month grids
// line up with the string, then re-flattened via localDateStr before ever
// leaving the component.
const parseLocalDate = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const buildMonthCells = (year, month) => {
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = Array(firstWeekday).fill(null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(year, month, day));
  }
  return cells;
};

const formatRangeLabel = (fromDate, toDate) => {
  if (!fromDate && !toDate) return 'All dates';
  const opts = { month: 'short', day: 'numeric', year: 'numeric' };
  const from = fromDate ? parseLocalDate(fromDate).toLocaleDateString(undefined, opts) : '…';
  const to = toDate ? parseLocalDate(toDate).toLocaleDateString(undefined, opts) : '…';
  return !toDate || fromDate === toDate ? from : `${from} – ${to}`;
};

// Two-month range picker: click a start day, then an end day (or the same
// day twice for a single-day range); Apply commits the draft selection to
// fromDate/toDate, Cancel discards it. Replaces a pair of native date
// inputs with one dropdown so picking a range is a single interaction.
function DateRangePicker({ fromDate, toDate, onApply, maxDate }) {
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(fromDate || null);
  const [draftTo, setDraftTo] = useState(toDate || null);
  const [viewDate, setViewDate] = useState(() => parseLocalDate(fromDate) || new Date());
  const [align, setAlign] = useState('left');
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const handleTriggerClick = () => {
    if (open) {
      setOpen(false);
      return;
    }
    setDraftFrom(fromDate || null);
    setDraftTo(toDate || null);
    setViewDate(parseLocalDate(fromDate) || new Date());
    // Decided once, off the trigger's own position — not the popover's, which
    // would depend on the alignment being decided and could oscillate.
    const triggerRect = containerRef.current?.getBoundingClientRect();
    const popoverWidth = Math.min(window.innerWidth * 0.94, 560);
    setAlign(triggerRect && triggerRect.left + popoverWidth > window.innerWidth - 8 ? 'right' : 'left');
    setOpen(true);
  };

  const handleDayClick = (date) => {
    const value = localDateStr(date);
    if (maxDate && value > maxDate) return;
    if (!draftFrom || draftTo) {
      setDraftFrom(value);
      setDraftTo(null);
    } else if (value < draftFrom) {
      setDraftTo(draftFrom);
      setDraftFrom(value);
    } else {
      setDraftTo(value);
    }
  };

  const handleApply = () => {
    onApply(draftFrom || '', draftTo || draftFrom || '');
    setOpen(false);
  };

  const leftYear = viewDate.getFullYear();
  const leftMonth = viewDate.getMonth();
  const rightRef = new Date(leftYear, leftMonth + 1, 1);
  const rightYear = rightRef.getFullYear();
  const rightMonth = rightRef.getMonth();
  const todayStr = localDateStr(new Date());

  const renderMonthGrid = (year, month) => (
    <div className="date-range-month">
      <div className="date-range-weekdays">
        {WEEKDAY_LABELS.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
      <div className="date-range-days">
        {buildMonthCells(year, month).map((date, index) => {
          if (!date) return <span key={`blank-${index}`} className="date-range-cell" />;
          const value = localDateStr(date);
          const isStart = value === draftFrom;
          const isEnd = value === draftTo;
          const inRange = draftFrom && draftTo && value > draftFrom && value < draftTo;
          const isDisabled = Boolean(maxDate && value > maxDate);
          return (
            <span
              key={value}
              className={[
                'date-range-cell',
                isStart ? 'range-start' : '',
                isEnd ? 'range-end' : '',
                inRange ? 'in-range' : '',
                value === todayStr ? 'is-today' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <button
                type="button"
                className="date-range-day"
                disabled={isDisabled}
                onClick={() => handleDayClick(date)}
              >
                {date.getDate()}
              </button>
            </span>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="date-range-picker" ref={containerRef}>
      <button type="button" className="date-range-trigger" onClick={handleTriggerClick}>
        <CalendarIcon width={15} height={15} />
        <span>{formatRangeLabel(fromDate, toDate)}</span>
      </button>
      {open && (
        <div className={`date-range-popover ${align === 'right' ? 'align-right' : ''}`}>
          <div className="date-range-nav-row">
            <button
              type="button"
              className="date-range-nav"
              onClick={() => setViewDate(new Date(leftYear, leftMonth - 1, 1))}
              aria-label="Previous month"
            >
              <ChevronLeftIcon width={16} height={16} />
            </button>
            <div className="date-range-titles">
              <span>{MONTH_LABELS[leftMonth]} {leftYear}</span>
              <span>{MONTH_LABELS[rightMonth]} {rightYear}</span>
            </div>
            <button
              type="button"
              className="date-range-nav"
              onClick={() => setViewDate(new Date(leftYear, leftMonth + 1, 1))}
              aria-label="Next month"
            >
              <ChevronRightIcon width={16} height={16} />
            </button>
          </div>
          <div className="date-range-months">
            {renderMonthGrid(leftYear, leftMonth)}
            {renderMonthGrid(rightYear, rightMonth)}
          </div>
          <div className="date-range-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handleApply} disabled={!draftFrom}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DateRangePicker;
